import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Pencil } from 'lucide-react';
import { FcCalendar } from 'react-icons/fc';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { ColumnType, DeliveryState, GetDeliveryQuery, TotadminPermission } from 'data/__generated__';

import { CopyValue } from 'components/CopyValue';
import { DynamicDeliveryTag } from 'components/DynamicDeliveryTag';

import { colors } from 'constants/colors';
import { formatDateForDisplay, getExtendedFormattedDatetime, getTimeFromDatetime } from 'helpers/dateTimes';

import { UpdateDeliveryDatePopup } from './UpdateDeliveryDatePopup';

type Delivery = NonNullable<GetDeliveryQuery['delivery']>;
type DeliveredColumn = Delivery['deliveredMicrostoreColumns'][number];

// Secured/Unsecured microstore columns have a dedicated MS Colonne detail page; other types
// (e.g. VirtualMicrostore) aren't served by it, so fall back to the generic column stocks page.
const getColumnDetailPath = ({ _id, type }: DeliveredColumn): string =>
    type === ColumnType.SecuredMicrostore || type === ColumnType.UnsecuredMicrostore
        ? `/microstore-column/${_id}`
        : `/column/${_id}/stocks`;

const EDITABLE_DATE_STATES = [
    DeliveryState.Created,
    DeliveryState.Packaging,
    DeliveryState.Ready,
    DeliveryState.NotDelivered,
    DeliveryState.InProgress,
];

/**
 * Ce qu'on vient chercher en ouvrant une livraison : où, quand, par qui, avec quoi.
 *
 * Remplace les sections « Informations générales » et « Informations de livraison », qui étalaient
 * les mêmes faits sur cinq colonnes et deux blocs dépliables. L'identité (site, date) est portée par
 * le fil d'Ariane, les identifiants techniques par la ligne de métadonnées en pied.
 */
export const DeliveryKeyFacts = ({ delivery }: { delivery: Delivery }) => {
    const {
        _id: deliveryId,
        createdAt,
        deliveredMicrostoreColumns,
        deliveryDate,
        deliveryPerson,
        isDynamic,
        orders,
        roundColor,
        roundName,
        serviceTime,
        siteId,
        siteName,
        siteSetup,
        state,
        theoricServiceTime,
        times: {
            preferenceSlot: { start: startTimePreference, end: endTimePreference },
            actualSlot: { start: actualStartTime, end: actualEndTime },
        },
        totem,
        trackingUrl,
        updatedAt,
    } = delivery;
    const [isUpdateDatePopupOpen, setIsUpdateDatePopupOpen] = useState(false);

    const deliveryPersonName =
        deliveryPerson?.profile.lastname || deliveryPerson?.profile.firstname
            ? `${deliveryPerson.profile.lastname ?? ''} ${deliveryPerson.profile.firstname ?? ''}`.trim()
            : (deliveryPerson?.profile.fullname ?? deliveryPerson?.username);

    return (
        <div
            className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3"
            data-test="delivery-key-facts"
        >
            <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-6 gap-y-4">
                <Fact label="Site">
                    <Link className="font-medium text-primary hover:underline" to={`/site/${siteId}`}>
                        {siteName}
                    </Link>
                </Fact>
                <Fact label="Totem" title={totem ? `${totem.name} (n°${totem.number})` : undefined}>
                    {totem ? `${totem.name} (n°${totem.number})` : <Empty />}
                </Fact>
                <Fact label="Date de livraison" span={2} wrap>
                    <FcCalendar size={18} />
                    <span className="font-medium capitalize">
                        {formatDateForDisplay(deliveryDate, { displayDay: true })}
                    </span>
                    <DynamicDeliveryTag isDynamic={isDynamic} label="Livraison dynamique" />
                    {EDITABLE_DATE_STATES.includes(state) ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setIsUpdateDatePopupOpen(true)}
                            requiredPermissions={[TotadminPermission.DeliveriesManage]}
                            data-test="update-delivery-date"
                        >
                            <Pencil />
                            Modifier
                        </Button>
                    ) : null}
                </Fact>
                <Fact label="Tournée">
                    <span
                        className="inline-block rounded px-2 py-0.5 font-medium"
                        style={{ backgroundColor: roundColor || colors.pantinGrey, color: colors.pureWhite }}
                    >
                        {roundName || 'Aucune'}
                    </span>
                </Fact>
                <Fact label="Livreur">
                    {deliveryPerson ? (
                        <Link className="text-primary hover:underline" to={`/user/${deliveryPerson._id}`}>
                            {deliveryPersonName}
                        </Link>
                    ) : (
                        <Empty />
                    )}
                </Fact>
                <Fact label="Créneau souhaité">
                    {startTimePreference && endTimePreference ? (
                        `${getTimeFromDatetime(new Date(startTimePreference))} – ${getTimeFromDatetime(
                            new Date(endTimePreference),
                        )}`
                    ) : (
                        <Empty label="Pas de préférence" />
                    )}
                </Fact>
                <Fact label="Arrivée → clôture">
                    {actualStartTime || actualEndTime ? (
                        `${actualStartTime ? getTimeFromDatetime(new Date(actualStartTime)) : '—'} → ${
                            actualEndTime ? getTimeFromDatetime(new Date(actualEndTime)) : '—'
                        }`
                    ) : (
                        <Empty />
                    )}
                </Fact>
                <Fact label="Temps de service prévu / effectif">
                    {formatMinutes(theoricServiceTime)} <span className="text-muted-foreground">/</span>{' '}
                    {formatMinutes(serviceTime)}
                </Fact>
                <Fact label="Commandes" wrap>
                    {orders.length ? (
                        <span className="flex flex-wrap gap-x-2">
                            {orders.map((order) => (
                                <Link
                                    key={order._id}
                                    className="text-primary hover:underline"
                                    to={`/order/${order._id}`}
                                >
                                    n°{order.number}
                                </Link>
                            ))}
                        </span>
                    ) : (
                        <Empty label="Aucune" />
                    )}
                </Fact>
                <Fact label="Colonnes livrées" wrap span={deliveredMicrostoreColumns.length > 1 ? 2 : 1}>
                    {deliveredMicrostoreColumns.length ? (
                        <span className="flex flex-wrap gap-x-2">
                            {deliveredMicrostoreColumns.map((column) => (
                                <Link
                                    key={column._id}
                                    className="text-primary hover:underline"
                                    to={getColumnDetailPath(column)}
                                >
                                    {column.name}
                                </Link>
                            ))}
                        </span>
                    ) : (
                        <Empty label="Aucune" />
                    )}
                </Fact>
                <Fact label="Rangement Totops">
                    {siteSetup ? (
                        <Link className="text-primary hover:underline" to={`/site-setup/${siteSetup._id}`}>
                            Lien
                        </Link>
                    ) : (
                        <Empty />
                    )}
                </Fact>
                <Fact label="Lien de suivi">
                    {trackingUrl ? (
                        <a
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                            href={trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Suivre la tournée
                            <ExternalLink className="size-3" />
                        </a>
                    ) : (
                        <Empty />
                    )}
                </Fact>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-border pt-2 text-xs text-muted-foreground">
                <CopyValue value={deliveryId} style={{ fontSize: '12px' }} />
                <span>Créée le {getExtendedFormattedDatetime(createdAt)}</span>
                <span>Mise à jour le {getExtendedFormattedDatetime(updatedAt)}</span>
            </div>

            <UpdateDeliveryDatePopup
                deliveryId={deliveryId}
                isOpen={isUpdateDatePopupOpen}
                setIsOpen={setIsUpdateDatePopupOpen}
            />
        </div>
    );
};

/** `wrap` pour une valeur composite (boutons, listes de liens) : sans lui, la valeur est tronquée. */
const Fact = ({
    label,
    children,
    span = 1,
    wrap = false,
    title,
}: {
    label: string;
    children: ReactNode;
    span?: 1 | 2;
    wrap?: boolean;
    title?: string;
}) => (
    <div className={cn('flex min-w-0 flex-col gap-0.5', span === 2 && 'sm:col-span-2')}>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <span
            className={cn('text-sm', wrap ? 'flex flex-wrap items-center gap-x-2 gap-y-1' : 'truncate')}
            title={title}
        >
            {children}
        </span>
    </div>
);

const formatMinutes = (minutes?: number | null) => (minutes === null || minutes === undefined ? '—' : `${minutes} min`);

const Empty = ({ label = 'Non défini' }: { label?: string }) => <span className="text-muted-foreground">{label}</span>;
